"use client";
import { OrderType, ServiceType } from "@/constants/enum";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import { Address } from "@/dto/address";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import {
  ScheduleDateType,
  ScheduledTime,
  ScheduleTimeType,
} from "@/dto/scheduledTime";
import { Service } from "@/dto/service";
import { Cart } from "@/models/cart/cart";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { RestaurantCartItem } from "@/models/cart/types";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { getFormattedScheduledOrderDisplay } from "@/utils/dateTimeUtils";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import pom from "@/utils/pom";
import { calculatePickupTime } from "@/utils/restaurantCartUtils";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import useLocalStorage from "@/utils/useLocalStorage";
import useMediaQuery from "@/utils/useMediaQuery";
import { isEmpty } from "lodash";
import { DateTime } from "luxon";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next13-progressbar";
import React, { useEffect, useMemo, useState } from "react";
import { DrawerModal } from "../modals/modals";
import { RestaurantCartItemView } from "./restaurantCartItemView";
import { RestaurantCartScheduledOrderForm } from "./restaurantCartScheduledOrderForm";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { useAddressDialog } from "@/utils/useAddressDialog";
import { useAuth } from "@/app/providers/authProvider";

interface RestaurantCartDrawerProps {
  isOpen?: boolean;
  onClose: () => void;
  onRequestClose: () => void;
  merchant: Merchant;
  // eslint-disable-next-line no-unused-vars
  onTriggerLoginModal: (toCheckout: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  setDeliveryAddressModalOpen: (value: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  setIsRestaurantCartOpen: (value?: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  setDeliveryAddressErrorModalOpen: (value: boolean) => void;
  selectedDayPart?: DayPart | null;
}

const extractHashFromUrl = (url: string) => {
  const urlObj = new URL(url);
  return urlObj.hash;
};

export const RestaurantCartDrawer = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  onTriggerLoginModal,
  setDeliveryAddressModalOpen,
  setIsRestaurantCartOpen,
  setDeliveryAddressErrorModalOpen,
  selectedDayPart,
}: RestaurantCartDrawerProps) => {
  const hash =
    globalThis.window === undefined
      ? ""
      : extractHashFromUrl(globalThis?.location?.href);
  const pathName = usePathname();
  const tUtils = useTranslations("CalculatePickTime");
  const locale = useLocale();
  const format = useFormatter();
  const { isLoggedIn } = useAuth();
  const openAddressFlow = useAddressDialog(setDeliveryAddressModalOpen);

  const searchParams = useSearchParams();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const merchantMeta = merchant.merchantMeta;
  const merchantColor =
    merchantMeta?.colorPalette?.color || cloverColorTheme.Main.primaryColor;
  const merchantService = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const curbsideService = merchant.services.find(
    (s: Service) => s.type === ServiceType.CURBSIDE
  );
  const deliveryService = merchant?.services.find(
    (s: Service) => s.type === ServiceType.DELIVERY
  );
  const now = DateTime.now().setZone(merchant?.timezone);
  const router = useRouter();
  const [cartItems] = useLocalStorage<RestaurantCartItem[]>(
    LocalStorageKey.RESTAURANT_CART,
    []
  );
  const [scheduledOrder, setScheduledOrder] = useState<any>();
  const [orderType, setOrderType] = useState<OrderType>(() => {
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );

    const savedOrderType = sessionTime.orderType;

    const restaurantServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.CURBSIDE, service: curbsideService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];
    const enabledServices = restaurantServices.filter(
      ({ service }) => service?.enabled
    );

    if (
      savedOrderType &&
      enabledServices.find(
        (s: { type: OrderType | undefined; service: Service | undefined }) =>
          s.type === savedOrderType
      )
    ) {
      return savedOrderType;
    } else {
      return enabledServices.length > 0
        ? enabledServices[0]?.type
        : OrderType.PICKUP;
    }
  });
  const [showScheduledOrderForm, setShowScheduledOrderForm] =
    useState<boolean>(false);
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: tUtils("today"),
    dateObject: now.toJSDate(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now.toJSDate()),
  });
  const [giftCard, setGiftCard] = useLocalStorage<any>(
    LocalStorageKey.GIFT_CARD,
    null
  );
  /**
   * REFACTOR: For now moving away from hook and getting directly from local storage
   * Current hook need to be refactored as it returns the state with initial value first
   * and then state getting updated in useEffect returning later in render cycle causing issues
   * Need to refactor useLocalStorage hook to leverage useSyncExternalStore
   */
  const deliveryAddress = getFromLocalStorage<Partial<Address>>(
    LocalStorageKey.DELIVERY_ADDRESS,
    {}
  );

  const businessHours = merchantService?.hours;
  const allDaysEmpty =
    businessHours &&
    Object.values(businessHours).every((day: any) => day.length === 0);
  const isMinimumMet = () => {
    const totalPrice =
      cartItems.reduce(
        (total: number, i: any) => (total += i.value.total * i.value.quantity),
        0
      ) / 100;

    return merchant.deliveryMinimum / 100 <= totalPrice;
  };
  const businessOpen = useMemo(() => isBusinessOpen(merchant), [merchant]);
  const hasItemsInCart = cartItems.length > 0;
  let checkoutDisabled =
    (cartItems.length == 0 && !giftCard) ||
    (!businessOpen && hasItemsInCart) ||
    !businessHours ||
    allDaysEmpty ||
    (cartItems.length > 0 &&
      orderType === OrderType.DELIVERY &&
      !isMinimumMet()) ||
    scheduledOrder == null;
  const customerAuthEnabled: boolean =
    process.env.NEXT_PUBLIC_AUTH_ENABLED === "true";

  const calculatePickupTimeAndStoreInSession = () => {
    const pickupTimeData = calculatePickupTime(
      now,
      businessHours,
      merchant.leadTime,
      merchant.timezone,
      orderType,
      merchant?.scheduledOrdering?.maxDays,
      selectedDayPart,
      tUtils
    );

    setScheduledOrder(pickupTimeData);
    saveToSessionStorage(SessionStorageKey.PICKUP_TIME, pickupTimeData);
  };

  useEffect(() => {
    /**
     * On changing order type, pickup time can be changed. For delivery type pickup time need to consider delivery delay for showing earliest schedule slot in
     * for when? dropdown where as for in-store/curbside pickup delivery delay will not be taken into consideration
     */
    if (isOpen && businessHours) {
      calculatePickupTimeAndStoreInSession();
      setScheduleTime({
        displayValue: "",
        timeObject: new Date(now.toJSDate()),
      });
    }
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      date: sessionTime?.date || null,
      time: sessionTime?.time || null,
      ...(sessionTime?.asap && { asap: true }),
    });
    const canAccessConfirmation = getFromSessionStorage<boolean | undefined>(
      SessionStorageKey.CAN_ACCESS_CONFIRMATION,
      undefined
    );
    const fetchEstimate = async () => {
      if (orderType === OrderType.DELIVERY) {
        if (deliveryAddress?.address1) {
          try {
            const result = await getOrderEstimate(merchant.id, deliveryAddress);
            saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, result);
            // This event fires when the order type changes to delivery
            // and there is a delivery address saved in local storage
            if (!canAccessConfirmation) {
              globalThis.dispatchEvent(
                new StorageEvent("storage", {
                  key: LocalStorageKey.RESTAURANT_CART_SYNC,
                  newValue: JSON.stringify(cartItems),
                })
              );
            }
          } catch {
            setDeliveryAddressModalOpen(false);
            setDeliveryAddressErrorModalOpen(true);
            onRequestClose();
          }
        } else {
          openAddressFlow();
          setIsRestaurantCartOpen(false);
        }
      }
    };
    fetchEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  useEffect(() => {
    if (isOpen && businessHours) {
      calculatePickupTimeAndStoreInSession();
    }
  }, [
    isOpen,
    businessHours,
    merchant.leadTime,
    merchant.timezone,
    selectedDayPart,
  ]);

  useEffect(() => {
    // this code works only when window storage event was dispatched from within
    // RestaurantItemModalModifierSelection component, handleAddToCart function
    const handleStorageChange = (event: StorageEvent) => {
      if (isMobileScreen) return;
      if (event.key === LocalStorageKey.DELIVERY_ORDER_TYPE) {
        setOrderType(OrderType.DELIVERY);
      } else if (event.key === LocalStorageKey.PICKUP_ORDER_TYPE) {
        setOrderType(OrderType.PICKUP);
      }
    };
    globalThis.addEventListener("storage", handleStorageChange);
    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileScreen]);

  useEffect(() => {
    if (isOpen) {
      const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
        SessionStorageKey.SCHEDULED_TIME,
        {}
      );
      setOrderType(sessionTime?.orderType || OrderType.PICKUP);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const removeFromCart = (event: React.MouseEvent, index: any) => {
    event.stopPropagation();
    const itemToRemove = cartItems[index];
    if (itemToRemove) {
      RestaurantCart.removeItem(itemToRemove.key);
    }
  };

  useEffect(() => {
    if (isEmpty(selectedDayPart)) return;

    setScheduleTime({
      displayValue: "",
      timeObject: new Date(now.toJSDate()),
    });
    setScheduleDate({
      displayValue: tUtils("today"),
      dateObject: now.toJSDate(),
    });
  }, [selectedDayPart]);

  const handleBackToCart = () => {
    setShowScheduledOrderForm(false);

    calculatePickupTimeAndStoreInSession();
  };

  const handleConfirmScheduledOrder = () => {
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      date: scheduleDate,
      time: scheduleTime,
    });

    setShowScheduledOrderForm(false);

    const merchantNow = DateTime.now()
      .setZone(merchant?.timezone)
      .setLocale(locale);

    const selectedDateDt = DateTime.fromJSDate(scheduleDate.dateObject)
      .setZone(merchant?.timezone)
      .setLocale(locale);

    const selectedTimeDt = DateTime.fromJSDate(scheduleTime.timeObject)
      .setZone(merchant?.timezone)
      .setLocale(locale);

    const formattedDate = format.dateTime(selectedDateDt.toJSDate(), {
      month: "short",
      day: "numeric",
      timeZone: merchant?.timezone,
    });

    const formattedTime = selectedTimeDt.toLocaleString(DateTime.TIME_SIMPLE);

    // Timezone-accurate Today/Tomorrow checks
    const isToday = selectedDateDt.hasSame(merchantNow, "day");
    const isTomorrow = selectedDateDt.hasSame(
      merchantNow.plus({ days: 1 }),
      "day"
    );

    setScheduledOrder(
      getFormattedScheduledOrderDisplay(
        isToday,
        isTomorrow,
        formattedDate,
        formattedTime,
        tUtils
      )
    );
  };

  const handleContinueToCheckout = () => {
    if (customerAuthEnabled) {
      if (isLoggedIn) {
        router.push("/checkout");
        onRequestClose();
      } else {
        onRequestClose();
        onTriggerLoginModal(true);
      }
    } else {
      router.push("/guest-checkout");
    }
  };

  const requestCloseHandler = () => {
    setShowScheduledOrderForm(false);
    Cart.removeHashFromURL(hash, pathName, searchParams);
    onRequestClose();
  };

  if (showScheduledOrderForm) {
    return (
      <DrawerModal
        isOpen={isOpen ?? false}
        onRequestClose={requestCloseHandler}
        contentClassName="flex flex-col h-full w-[100%]"
        data-testid={applyDataTestId(pom.restaurantCart.cartDrawer)}
        hideCloseButton={true}
      >
        <RestaurantCartScheduledOrderForm
          {...{
            merchant,
            businessHours,
            handleBackToCart,
            scheduleDate,
            setScheduleDate,
            allDaysEmpty,
            scheduleTime,
            setScheduleTime,
            merchantColor,
            handleConfirmScheduledOrder,
            orderType,
            selectedDayPart,
            isOpen,
          }}
        />
      </DrawerModal>
    );
  }

  return (
    <RestaurantCartItemView
      {...{
        isOpen,
        onClose,
        merchant,
        hash,
        cartItems,
        onRequestClose: requestCloseHandler,
        orderType,
        setOrderType,
        scheduledOrder,
        setScheduledOrder,
        setShowScheduledOrderForm,
        removeFromCart,
        checkoutDisabled,
        merchantColor,
        handleContinueToCheckout,
        setIsRestaurantCartOpen,
        giftCard,
        setGiftCard,
        selectedDayPart,
      }}
    />
  );
};
